// src/features/reader/ui/pages/ReaderPatreonAuthGatePage.tsx
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as patreonApi from '../../../../api/patreon';
import { Alert, Box, Button } from '@mui/material';
import axios from 'axios';
import { saveNextUrl } from '../../../auth/domain/authNavigation';

function getAxiosError(err: unknown): { status?: number; message?: string } {
  if (!axios.isAxiosError(err)) {
    return { message: 'A server error occurred. Please try again later.' };
  }

  const status = err.response?.status;

  const message =
    err.response?.data?.error ||
    err.response?.data?.message ||
    err.response?.statusText ||
    'A server error occurred. Please try again later.';

  return { status, message };
}

export default function ReaderPatreonAuthGatePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const ranOnce = useRef(false);

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    if (ranOnce.current) return;
    ranOnce.current = true;

    const next = `/g/${id}`;
    saveNextUrl(next);

    const run = async () => {
      try {
        await patreonApi.patreonIsAuth();
        nav(next, { replace: true });
        return;
      } catch (err: any) {
        const { status, message } = getAxiosError(err);

        if (status === 401) {
          try {
            const r = await patreonApi.getPatreonAuthUrl();
            const url = r.data?.url;

            if (url) {
              window.location.replace(url);
              return;
            }

            setErrorMsg('Could not start Patreon authentication.');
            return;
          } catch (e) {
            const { message } = getAxiosError(e);
            setErrorMsg(message || 'Could not start Patreon authentication.');
            return;
          }
        }

        setErrorMsg(message || 'Failed to validate Patreon authentication.');
      }
    };

    void run();
  }, [id, nav]);

  if (errorMsg) {
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 560, width: '100%' }}>
          {errorMsg}
        </Alert>

        <Button
          variant="contained"
          onClick={() => nav('/', { replace: true })}
          sx={{ minWidth: 160 }}
        >
          Back
        </Button>
        <Button
          variant="outlined"
          onClick={() => nav('/login', { replace: true })}
          sx={{ minWidth: 160 }}
        >
          Sign in options
        </Button>
      </Box>
    );
  }

  return null;
}
